import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { groupTripApi } from '../../api/groupTripApi';
import GroupTripDraftForm from './GroupTripDraftForm';
import {
  buildUpdateDraftPayload,
  canHostEditGroupTrip,
  classifyGroupTripMutationError,
  draftFormValuesFromTrip,
  groupTripErrorMessage,
  normalizeGroupTrip,
  validateGroupTripDraftForm,
} from './groupTripModel';
import './group-trip.css';

export default function EditGroupTripPage() {
  const { groupTripId } = useParams();
  const { t } = useTravelTranslation();
  const navigate = useNavigate();
  const formId = useId();
  const titleId = useId();
  const submitting = useRef(false);
  const generation = useRef(0);

  const [trip, setTrip] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [needsReload, setNeedsReload] = useState(false);

  const [recruitmentTitle, setRecruitmentTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [capacity, setCapacity] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [visibility, setVisibility] = useState('PUBLIC');

  const hydrateForm = useCallback((detail) => {
    const values = draftFormValuesFromTrip(detail);
    setRecruitmentTitle(values.recruitmentTitle);
    setSummary(values.summary);
    setCapacity(values.capacity);
    setStartAt(values.startAt);
    setEndAt(values.endAt);
    setVisibility(values.visibility);
  }, []);

  const load = useCallback(async () => {
    const token = ++generation.current;
    if (!/^[1-9]\d*$/.test(groupTripId || '')) {
      setPhase('error');
      setError(t('travel.groupTrip.notFound'));
      return;
    }
    setPhase('loading');
    setError('');
    setFormError('');
    setNeedsReload(false);
    try {
      const detail = normalizeGroupTrip(await groupTripApi.get(groupTripId), t);
      if (token !== generation.current) return;
      setTrip(detail);
      hydrateForm(detail);
      setPhase('ready');
    } catch (cause) {
      if (token !== generation.current) return;
      setError(groupTripErrorMessage(cause, t));
      setPhase('error');
    }
  }, [groupTripId, hydrateForm, t]);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  async function onSubmit(event) {
    event.preventDefault();
    if (!trip || submitting.current || busy) return;
    const validationError = validateGroupTripDraftForm({
      recruitmentTitle,
      summary,
      capacity,
      startAt,
      endAt,
      visibility,
    }, t, trip);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    submitting.current = true;
    setBusy(true);
    setFormError('');
    setError('');
    try {
      const payload = buildUpdateDraftPayload(trip, {
        recruitmentTitle,
        summary,
        capacity,
        startAt,
        endAt,
        visibility,
      });
      const updated = await groupTripApi.updateDraft(trip.groupTripId, payload);
      navigate(`/group-trips/${trip.groupTripId}`, {
        state: { updatedTrip: normalizeGroupTrip(updated, t) },
      });
    } catch (cause) {
      const kind = classifyGroupTripMutationError(cause);
      if (kind === 'conflict') {
        setNeedsReload(true);
      }
      setFormError(groupTripErrorMessage(cause, t, 'travel.groupTrip.updateFailed'));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  if (phase === 'loading') {
    return <main className="group-trips"><p>{t('travel.common.loading')}</p></main>;
  }

  if (phase === 'error' && !trip) {
    return (
      <main className="group-trips" role="alert">
        <p>{error}</p>
        <button type="button" onClick={load}>{t('travel.common.retry')}</button>
        <Link to="/group-trips">{t('travel.groupTrip.backToList')}</Link>
      </main>
    );
  }

  if (trip && !canHostEditGroupTrip(trip)) {
    return (
      <main className="group-trips">
        <header className="group-trips__intro">
          <h1>{t('travel.groupTrip.editTitle')}</h1>
          <p className="group-trips__hint">{t('travel.groupTrip.editOnlyDraftHint')}</p>
          <Link to={`/group-trips/${trip.groupTripId}`}>{t('travel.groupTrip.backToDetail')}</Link>
        </header>
      </main>
    );
  }

  return (
    <main className="group-trips">
      <header className="group-trips__intro">
        <p>{t('travel.common.host')}</p>
        <h1 id={titleId}>{t('travel.groupTrip.editTitle')}</h1>
        <span>{t('travel.groupTrip.editHint')}</span>
        <Link to={`/group-trips/${trip.groupTripId}`}>{t('travel.groupTrip.cancelEdit')}</Link>
      </header>
      <form className="group-trips__panel" id={formId} onSubmit={onSubmit} aria-labelledby={titleId}>
        <GroupTripDraftForm
          formId={formId}
          publicationId={String(trip.journeyPublicationId || '')}
          recruitmentTitle={recruitmentTitle}
          onRecruitmentTitleChange={setRecruitmentTitle}
          summary={summary}
          onSummaryChange={setSummary}
          capacity={capacity}
          onCapacityChange={setCapacity}
          startAt={startAt}
          onStartAtChange={setStartAt}
          endAt={endAt}
          onEndAtChange={setEndAt}
          visibility={visibility}
          onVisibilityChange={setVisibility}
          disabled={busy}
          datesLocked
          t={t}
        />
        {formError ? <p className="group-trips__error" role="alert">{formError}</p> : null}
        {needsReload ? (
          <button type="button" onClick={load} disabled={busy}>{t('travel.groupTrip.reloadAndRetry')}</button>
        ) : null}
        <div className="group-trips__actions">
          <button type="submit" disabled={busy} aria-busy={busy}>
            {busy ? t('travel.groupTrip.saving') : t('travel.groupTrip.saveDraft')}
          </button>
          <Link to={`/group-trips/${trip.groupTripId}`}>{t('travel.groupTrip.cancelEdit')}</Link>
        </div>
      </form>
    </main>
  );
}
